import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Library, ArrowLeft, BookOpen, Download, Star, Share2, Code, Copy, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressBar = ({ value = 0, className = '' }) => (
  <div className={`w-full bg-muted rounded-full h-2 overflow-hidden ${className}`} data-testid="progress-bar">
    <div
      className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

const StarRating = ({ rating, onRate, interactive = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
        >
          <Star className={`h-5 w-5 ${(hover || rating) >= star ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
        </button>
      ))}
    </div>
  );
};

const ReviewSection = ({ bookId, token }) => {
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/books/${bookId}/reviews`);
      setReviews(res.data.reviews || []);
      setAvgRating(res.data.average_rating || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [bookId]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSubmit = async () => {
    if (myRating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/books/${bookId}/reviews`, { rating: myRating, comment: myComment || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review submitted');
      setMyRating(0); setMyComment('');
      fetchReviews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit review'); }
    setSubmitting(false);
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <StarRating rating={Math.round(avgRating)} />
        <span className="text-sm text-muted-foreground">{avgRating} / 5 ({reviews.length} review{reviews.length !== 1 ? 's' : ''})</span>
      </div>
      <div className="p-4 border border-border rounded-sm space-y-3">
        <Label className="text-sm font-medium">Your Review</Label>
        <StarRating rating={myRating} onRate={setMyRating} interactive />
        <Textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder="Share your thoughts about this book..." rows={2} data-testid="review-comment" />
        <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="submit-review-btn">
          {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          Submit Review
        </Button>
      </div>
      {reviews.length > 0 && (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.review_id} className="p-3 bg-muted/30 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">{r.user_name}</span>
                <StarRating rating={r.rating} />
                <span className="text-xs text-muted-foreground ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ShareButton = ({ book }) => {
  const [open, setOpen] = useState(false);
  const shareUrl = `${window.location.origin}/book/${book.book_id}`;
  const shareText = `Check out "${book.title}" by ${book.author}!`;

  const handleCopyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); setOpen(false); };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`share-btn-${book.book_id}`}>
        <Share2 className="h-4 w-4 mr-1" /> Share
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Share "{book.title}"</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={handleCopyLink} data-testid="share-copy-link"><Copy className="h-4 w-4 mr-2" />Copy Link</Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank'); setOpen(false); }}><span className="mr-2 font-bold">X</span>Share on Twitter/X</Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank'); setOpen(false); }}><span className="mr-2 font-bold">f</span>Share on Facebook</Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, '_blank'); setOpen(false); }}><span className="mr-2">WA</span>Share on WhatsApp</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ReadingProgressTracker = ({ book, token, onUpdate }) => {
  const [progress, setProgress] = useState(book.reading_progress || 0);
  const [currentPage, setCurrentPage] = useState(book.current_page || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/books/${book.book_id}/progress`, {
        progress,
        current_page: currentPage ? parseInt(currentPage) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Progress saved');
      if (onUpdate) onUpdate(book.book_id, progress);
    } catch (e) { toast.error('Failed to save progress'); }
    setSaving(false);
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-4 p-4 border border-border rounded-sm bg-muted/20">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          Reading Progress
        </Label>
        {book.last_read_at && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Last read {timeAgo(book.last_read_at)}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className="text-sm font-semibold text-primary">{progress}%</span>
        </div>
        <Slider
          value={[progress]}
          onValueChange={([val]) => setProgress(val)}
          max={100}
          step={1}
          className="w-full"
          data-testid="progress-slider"
        />
      </div>

      <div className="flex items-end gap-3">
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-muted-foreground">Current Page</Label>
          <Input
            type="number"
            value={currentPage}
            onChange={(e) => setCurrentPage(e.target.value)}
            placeholder={book.pages ? `of ${book.pages}` : 'Page #'}
            className="h-8 text-sm"
            data-testid="current-page-input"
          />
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-progress-btn">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
        </Button>
      </div>

      {progress === 100 && (
        <p className="text-xs text-center text-green-600 font-medium">Finished! Great job!</p>
      )}
    </div>
  );
};

const BookReaderModal = ({ book, open, onClose, token, onProgressUpdate }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/books/${book.book_id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = book.file_name || `${book.title}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> {book.title}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue={book.embed_code && book.embed_enabled !== false ? 'read' : 'details'} className="flex-1 flex flex-col min-h-0">
          <TabsList className="shrink-0">
            {book.embed_code && book.embed_enabled !== false && (
              <TabsTrigger value="read" className="flex items-center gap-1"><Code className="h-3 w-3" />Read Online</TabsTrigger>
            )}
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="progress">Progress</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>

          {book.embed_code && book.embed_enabled !== false && (
            <TabsContent value="read" className="flex-1 min-h-0 overflow-hidden">
              <div className="w-full h-[60vh] rounded-sm overflow-hidden border border-border" dangerouslySetInnerHTML={{ __html: book.embed_code }} data-testid="book-embed-reader" />
            </TabsContent>
          )}

          <TabsContent value="details" className="overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <img src={book.cover_image} alt={book.title} className="w-24 h-36 object-cover rounded-sm" />
                <div>
                  <h3 className="font-heading text-xl font-semibold">{book.title}</h3>
                  <p className="text-muted-foreground">by {book.author}</p>
                  <p className="text-sm text-muted-foreground mt-1">{book.category}</p>
                  {book.pages && <p className="text-sm text-muted-foreground">{book.pages} pages</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <ProgressBar value={book.reading_progress || 0} className="w-32" />
                    <span className="text-xs text-muted-foreground">{book.reading_progress || 0}%</span>
                  </div>
                </div>
              </div>
              <p className="text-sm">{book.description}</p>
              <div className="flex gap-2">
                {book.has_file && book.download_enabled !== false && (
                  <Button onClick={handleDownload} disabled={downloading} data-testid={`download-btn-${book.book_id}`}>
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    Download {book.file_type?.toUpperCase() || 'File'}
                  </Button>
                )}
                <ShareButton book={book} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="progress" className="overflow-y-auto">
            <ReadingProgressTracker book={book} token={token} onUpdate={onProgressUpdate} />
          </TabsContent>

          <TabsContent value="reviews" className="overflow-y-auto">
            <ReviewSection bookId={book.book_id} token={token} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export const LibraryPage = () => {
  const { token } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/orders/library`, { headers, withCredentials: true });
        setBooks(response.data.books || []);
      } catch (error) { console.error('Error fetching library:', error); }
      setLoading(false);
    };
    fetchLibrary();
  }, [token]);

  const handleProgressUpdate = (bookId, progress) => {
    setBooks(prev => prev.map(b => b.book_id === bookId ? { ...b, reading_progress: progress } : b));
    if (selectedBook?.book_id === bookId) {
      setSelectedBook(prev => ({ ...prev, reading_progress: progress }));
    }
  };

  const handleDownload = async (book) => {
    try {
      const response = await axios.get(`${API}/books/${book.book_id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = book.file_name || `${book.title}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const getProgressLabel = (p) => {
    if (!p || p === 0) return 'Not started';
    if (p === 100) return 'Finished';
    return `${p}% read`;
  };

  const getProgressColor = (p) => {
    if (!p || p === 0) return 'text-muted-foreground';
    if (p === 100) return 'text-green-600';
    return 'text-primary';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="library-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" asChild>
            <Link to="/dashboard"><ArrowLeft className="h-4 w-4 mr-2" />Dashboard</Link>
          </Button>
        </div>

        <div className="mb-8">
          <p className="font-accent text-xs uppercase tracking-[0.2em] text-primary mb-2">Your Collection</p>
          <h1 className="font-heading text-4xl font-bold">My Library</h1>
        </div>

        {books.length === 0 ? (
          <div className="text-center py-16">
            <Library className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
            <h2 className="font-heading text-2xl font-semibold mb-2">Your library is empty</h2>
            <p className="text-muted-foreground mb-6">Purchase books to start building your collection</p>
            <Button asChild className="btn-lift"><Link to="/browse">Browse Books</Link></Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {books.map((book) => (
              <Card
                key={book.book_id}
                className="group overflow-hidden border-border hover:shadow-soft transition-all duration-300"
                data-testid={`library-book-${book.book_id}`}
              >
                <div className="relative aspect-[2/3] overflow-hidden bg-muted cursor-pointer" onClick={() => setSelectedBook(book)}>
                  <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover img-matte group-hover:scale-105 transition-transform duration-500" />
                  <Badge className="absolute top-3 left-3 font-accent text-[10px] uppercase tracking-wider bg-green-600 text-white">Owned</Badge>
                  {book.reading_progress === 100 && (
                    <Badge className="absolute top-3 right-3 font-accent text-[10px] uppercase tracking-wider bg-amber-500 text-white">Finished</Badge>
                  )}
                  {book.has_embed && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button variant="secondary" size="sm"><BookOpen className="h-4 w-4 mr-1" />Read Now</Button>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{book.category}</p>
                  <h3 className="font-heading text-lg font-semibold leading-tight mb-1">{book.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{book.author}</p>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${getProgressColor(book.reading_progress)}`}>
                        {getProgressLabel(book.reading_progress)}
                      </span>
                      {book.last_read_at && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Clock className="h-2.5 w-2.5" />
                          {new Date(book.last_read_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <ProgressBar value={book.reading_progress || 0} />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {book.has_file && book.download_enabled !== false && (
                      <Button variant="outline" size="sm" onClick={() => handleDownload(book)} data-testid={`library-download-${book.book_id}`}>
                        <Download className="h-3 w-3 mr-1" />Download
                      </Button>
                    )}
                    {book.has_embed && book.embed_enabled !== false && (
                      <Button variant="outline" size="sm" onClick={() => setSelectedBook(book)} data-testid={`library-read-${book.book_id}`}>
                        <BookOpen className="h-3 w-3 mr-1" />Read
                      </Button>
                    )}
                    <ShareButton book={book} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {selectedBook && (
          <BookReaderModal
            book={selectedBook}
            open={!!selectedBook}
            onClose={() => setSelectedBook(null)}
            token={token}
            onProgressUpdate={handleProgressUpdate}
          />
        )}
      </div>
    </div>
  );
};
